import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { io } from 'socket.io-client';
import { css, Theme, Global } from '@emotion/react';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { Provider as TooltipProvider } from '@radix-ui/react-tooltip';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { SOCKET_NAMESPACE_CLIENT } from 'common/CommonConstants';
import { AdminOutlet } from 'client/lib/AdminOutlet';
import { ErrorSnackbarProvider } from 'client/lib/Error';
import { Layout } from 'client/lib/Layout';
import { PrivateOutlet } from 'client/lib/PrivateOutlet';
import 'client/lib/script';
import { SocketContext } from 'client/lib/Socket';
import { TRPCProvider } from 'client/lib/trpc';
import { UserSessionProvider } from 'client/lib/User';
import { GitHubProvider } from 'client/state/GitHubState';
import { StatusTreeProvider } from 'client/state/useDeploymentStatusTree';
import { useUserState } from 'client/state/UserState';
import { InvitePage } from 'client/pages/auth/InvitePage';
import { LoginPage } from 'client/pages/auth/LoginPage';
import { DashboardPage } from 'client/pages/dashboard/DashboardPage';
import { CreateDeploymentPage } from 'client/pages/deployment/CreateDeploymentPage';
import { DeploymentLogPage } from 'client/pages/deployment/DeploymentLogPage';
import { DeploymentPage } from 'client/pages/deployment/DeploymentPage';
import { GitHubCallbackPage } from 'client/pages/github/GitHubCallbackPage';
import { ProfilePage } from 'client/pages/profile/ProfilePage';
import { ProjectListPage } from 'client/pages/project/ProjectListPage';
import { ProjectPage } from 'client/pages/project/ProjectPage';
import { StackBuilderPage } from 'client/pages/stack/StackBuilderPage';
import { AnonymousTrialLoginPage } from 'client/pages/trial/AnonymousTrialLoginPage';
import { AnonymousTrialQuickLaunchPage } from 'client/pages/trial/AnonymousTrialQuickLaunchPage';
import { AnonymousTrialPageLayout } from 'client/pages/trial/components/AnonymousTrialPageLayout';
import { ClaimAccountProvider } from 'client/components/Dialog/ClaimAccountDialog';
import { DeleteConfirmProvider } from 'client/components/Dialog/DeleteDialog';
import { LogDialog } from 'client/components/Log/LogDialog';
import { GradientsProvider } from 'client/components/Other/GradientsProvider';
import faviconCanary from 'client/favicon-canary.svg';
import faviconTest from 'client/favicon-test.svg';
import { muiTheme, darkTheme } from 'client/Theme';
import './index.css';

const AdminRoutes = lazy(() => import('client/pages/admin/AdminRoutes').then((module) => ({ default: module.AdminRoutes })));

const socket = io('wss://' + window.viteClientEnv.APP_HOSTNAME + SOCKET_NAMESPACE_CLIENT, { transports: ['websocket'] });
socket.on('connect_error', (err) => {
  console.log(`socket connect_error due to ${err.message}`);
});

// const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');

let lastTrackingUserID: number | null = null;

function App() {
  // const [isDark, setIsDark] = useState(colorSchemeQuery.matches);
  const [isLoggedIn, userID] = useUserState((state) => [state.loggedIn, state.user?.userID || null]);

  // useEffect(() => {
  //   const handleColorSchemeChange = (event: MediaQueryListEvent) => {
  //     setIsDark(event.matches);
  //   };

  //   colorSchemeQuery.addEventListener('change', handleColorSchemeChange);
  //   return () => colorSchemeQuery.removeEventListener('change', handleColorSchemeChange);
  // }, []);

  // const theme = isDark ? darkTheme : lightTheme;

  // Connect socket after successfull login
  useEffect(() => {
    if (isLoggedIn && socket.disconnected) socket.connect();

    if (userID && userID != lastTrackingUserID) {
      umami.identify({ userID });
      lastTrackingUserID = userID;
    }
  }, [isLoggedIn]);

  // Change favicon based on the environment
  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) return;

    const hostname = window.location.hostname;
    if (hostname.endsWith('.dev')) link.href = faviconTest;
    if (hostname.startsWith('canary')) link.href = faviconCanary;
  }, []);

  return (
    <ThemeProvider theme={muiTheme}>
      <EmotionThemeProvider theme={darkTheme}>
        <Global styles={globalStyles} />
        <GradientsProvider />
        <div css={bodyStyle}>
          <TooltipProvider skipDelayDuration={0}>
            <ErrorSnackbarProvider>
              <SocketContext.Provider value={socket}>
                <TRPCProvider>
                  <UserSessionProvider />
                  <GitHubProvider />
                  <StatusTreeProvider />
                  <DeleteConfirmProvider>
                    <ClaimAccountProvider>
                      <Routes>
                        {/* Callbacks */}
                        <Route path="github/*" element={<GitHubCallbackPage />} />

                        {/* Email invites */}
                        <Route path="invite/:token" element={<InvitePage />} />

                        {/* Authentication */}
                        <Route>
                          <Route path="sign-up" element={<LoginPage />} />
                          <Route path="login" element={<LoginPage />} />
                        </Route>

                        {/* Anonymous trial */}
                        <Route element={<AnonymousTrialPageLayout />}>
                          <Route path="launch/:token" element={<AnonymousTrialLoginPage />} />
                          <Route path="launch" element={<AnonymousTrialQuickLaunchPage />} />
                        </Route>

                        {/* Logs */}
                        <Route path=":account/project/:projectID/deployment/:deploymentID/logs/:type/" element={<DeploymentLogPage />}>
                          <Route index />
                          <Route path=":level1" />
                          <Route path=":level1/:level2" />
                          <Route path=":level1/:level2/:level3" />
                        </Route>

                        {/* Main application */}
                        <Route element={<Layout />}>
                          <Route element={<PrivateOutlet />}>
                            <Route index element={<DashboardPage />} />

                            <Route path="settings/*" element={<ProfilePage />} />

                            <Route path=":account/">
                              <Route index element={<Navigate replace to="projects" />} />

                              {/* Projects */}
                              <Route path="projects" element={<ProjectListPage />} />
                              <Route path="project/:projectID">
                                <Route index element={<ProjectPage />} />
                                <Route path="options" element={<ProjectPage path="options" />} />

                                {/* Deployments */}
                                <Route path="deployment/:cloneOfDeploymentID/clone" element={<CreateDeploymentPage />} />
                                <Route path="deployment/create" element={<CreateDeploymentPage />} />
                                <Route path="deployment/:deploymentID/*" element={<DeploymentPage />} />

                                {/* TODO: Need to add 404 page */}
                              </Route>

                              {/* Template */}
                              <Route path="template/create" element={<StackBuilderPage />} />
                            </Route>

                            {/* Admin */}
                            <Route element={<AdminOutlet />}>
                              <Route
                                path="admin/*"
                                element={
                                  <Suspense>
                                    <AdminRoutes />
                                  </Suspense>
                                }
                              />
                            </Route>
                          </Route>
                        </Route>

                        {/* 404 */}
                        {/* <Route path="*" element={<NotFoundPage />} /> */}
                      </Routes>

                      <LogDialog />
                    </ClaimAccountProvider>
                  </DeleteConfirmProvider>
                </TRPCProvider>
              </SocketContext.Provider>
            </ErrorSnackbarProvider>
          </TooltipProvider>
        </div>
      </EmotionThemeProvider>
    </ThemeProvider>
  );
}

export default App;

const globalStyles = (theme: Theme) => css`
  body {
    background-color: ${theme.color.background};
  }

  div[data-radix-popper-content-wrapper]:not(:has([data-radix-menu-content])) {
    pointer-events: none;
  }
`;

const bodyStyle = css`
  min-height: 100%;
`;
